import AnnotationIcon from "@assets/empty-annotation.svg";
import { css, useTheme } from "@emotion/react";
import Delete from "@mui/icons-material/DeleteTwoTone";
import InfoIcon from "@mui/icons-material/InfoTwoTone";
import { IconButton } from "@mui/material";
import { format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import type { GetAnnotationsOutput } from "../../../../api/src/types";
import { trpc } from "../../utils/trpc";
import { AnnotationView } from "../bits/Annotation";
import type { AnnotationWithTags } from "../bits/annotations/utils";
import { PrimaryButton } from "../bits/buttons";
import { FilterDropdown } from "../bits/FilterDropdown";
import {
  FlexColContainer,
  FlexRowContainer,
  SeparatorLineHorizontal,
} from "../bits/layout";
import { LoadingSpinner } from "../bits/loading";
import { Modal } from "../bits/Modal";
import { PublicationFilter } from "../bits/PublicationFilter";
import { NO_TAGS_OPTION } from "../bits/tags/constants";
import { TagManagementModal } from "../bits/tags/TagManagementModal";
import { TagsFilterMenu } from "../bits/TagsFilterMenu";
import { FilterHeader } from "../headers/FilterHeader";
import { useTitle } from "../hooks/useTitle";
import { AnnotationSearch } from "../patterns/AnnotationSearch";
import { FRJ, frjPublication } from "../bits/bookmarks/utils";

export const Annotations = () => {
  useTitle("Highlights & Notes | Compass");
  const theme = useTheme();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [queryValue, setQueryValue] = useState<string>("");
  const [selectedPublications, setSelectedPublications] = useState<
    { id: string | number; title: string }[]
  >([]);
  const [filteredAnnotations, setFilteredAnnotations] =
    useState<GetAnnotationsOutput>();
  const [groupedAnnotations, setGroupedAnnotations] = useState<
    Record<string, AnnotationWithTags[]>
  >({});
  const [selectedTags, setSelectedTags] = useState<
    { id: number | string; name: string }[]
  >([]);

  const { data: annotations, isLoading: annotationsLoading } =
    trpc.getAnnotations.useQuery(
      {},
      {
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
    );

  const deleteAnnotations = trpc.deleteAnnotations.useMutation({
    onSuccess: () => {
      setIsDeleteModalOpen(false);
    },
  });

  const { data: tags } = trpc.getTags.useQuery();

  const { data: publications, isLoading } = trpc.getPublications.useQuery();
  const isFetching = isLoading || annotationsLoading;
  const groupAnnotationsByDate = (annotationsToGroup: GetAnnotationsOutput) => {
    const sorted = [...annotationsToGroup].sort(
      (a, b) =>
        new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime(),
    );

    return sorted.reduce<Record<string, AnnotationWithTags[]>>(
      (acc, annotation) => {
        const dateKey = format(new Date(annotation.lastModified), "d MMMM");
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push({
          ...annotation,
          lastModified: new Date(annotation.lastModified),
          createdAt: new Date(annotation.createdAt),
        });
        return acc;
      },
      {},
    );
  };

  const cleanHtml = useCallback((html: string): string => {
    const container = document.createElement("div");
    container.innerHTML = html;

    // Remove elements with class "hidden"
    container.querySelectorAll(".hidden").forEach((el) => el.remove());

    // Get textContent, which ignores tag names, attributes, and removed elements
    return container.textContent?.toLowerCase() ?? "";
  }, []);

  useEffect(() => {
    if (filteredAnnotations?.length) {
      setGroupedAnnotations(groupAnnotationsByDate(filteredAnnotations));
    } else setGroupedAnnotations({});
  }, [filteredAnnotations]);

  useEffect(() => {
    if (
      annotations?.length &&
      (selectedPublications.length || queryValue || selectedTags.length)
    ) {
      let filtered: GetAnnotationsOutput = [...annotations];

      if (selectedPublications.length) {
        filtered = filtered.filter((annotation) => {
          if (
            selectedPublications.find((pub) => pub.id === FRJ) &&
            annotation.articleSlug
          ) {
            return true;
          }
          return selectedPublications.find(
            (pub) => annotation.publicationId === pub.id,
          );
        });
      }

      if (queryValue) {
        const query = queryValue.toLowerCase();
        filtered = filtered.filter((annotation) => {
          const noteText = (annotation.noteText || "").toLowerCase();
          const highlightedText = cleanHtml(annotation.highlightedText || "");
          return noteText.includes(query) || highlightedText.includes(query);
        });
      }

      if (selectedTags.length) {
        const hasNoTagsFilter = selectedTags.some(
          (tag) => tag.id === NO_TAGS_OPTION.id,
        );
        const specificTags = selectedTags.filter(
          (tag) => tag.id !== NO_TAGS_OPTION.id,
        );

        filtered = filtered.filter((annotation) => {
          const hasNoTags = annotation.tags.length === 0;

          const matchesSpecificTag = specificTags.some((tag) =>
            annotation.tags.some(
              (annotationTag) => annotationTag.tagId === tag.id,
            ),
          );

          if (hasNoTagsFilter && specificTags.length) {
            return hasNoTags || matchesSpecificTag;
          }

          if (hasNoTagsFilter) {
            return hasNoTags;
          }

          return matchesSpecificTag;
        });
      }
      setFilteredAnnotations(filtered);
    } else {
      setFilteredAnnotations(annotations ?? []);
    }
  }, [selectedPublications, annotations, queryValue, cleanHtml, selectedTags]);

  return (
    <>
      <FilterHeader>
        <FlexRowContainer
          css={({ breakpoints }) => css`
            @media (max-width: ${breakpoints.values.md}px) {
              display: none;
            }
          `}
        >
          <PublicationFilter
            publications={publications}
            selectedPublications={selectedPublications}
            setSelectedPublications={setSelectedPublications}
            includeFRJ
          />
        </FlexRowContainer>
        <FlexRowContainer
          css={({ breakpoints }) => css`
            @media (max-width: ${breakpoints.values.md}px) {
              display: none;
            }
          `}
        >
          <TagsFilterMenu
            tags={tags}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
          />
        </FlexRowContainer>
        <div
          css={({ breakpoints }) => css`
            display: none;
            @media (max-width: ${breakpoints.values.md}px) {
              display: flex;
            }
          `}
        >
          <FilterDropdown
            activeFilterCount={
              selectedPublications.length + selectedTags.length
            }
          >
            <PublicationFilter
              publications={publications}
              selectedPublications={selectedPublications}
              setSelectedPublications={setSelectedPublications}
              hideIcon
              includeFRJ
            />
            <TagsFilterMenu
              tags={tags}
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
              hideTagManagement
            />
          </FilterDropdown>
          <span
            css={({ breakpoints }) => css`
              align-items: center;
              display: flex;

              @media (max-width: ${breakpoints.values.sm}px) {
                display: none;
              }
            `}
          >
            Filter annotations
          </span>
        </div>
        <FlexRowContainer
          css={({ breakpoints }) => css`
            @media (max-width: ${breakpoints.values.md}px) {
              justify-content: flex-end;
            }
          `}
        >
          <AnnotationSearch
            queryValue={queryValue}
            setQueryValue={setQueryValue}
          />
          <div
            css={({ breakpoints }) => css`
              display: none;
              @media (max-width: ${breakpoints.values.md}px) {
                display: flex;
              }
            `}
          >
            <TagManagementModal tags={tags ?? []} buttonLabel="Manage Tags" />
          </div>

          <Modal
            key="deleteAnnotationModal"
            isOpen={isDeleteModalOpen}
            onClose={() => {
              setIsDeleteModalOpen(false);
            }}
            renderButton={() => (
              <IconButton
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={
                  deleteAnnotations.isLoading || !filteredAnnotations?.length
                }
                sx={{
                  borderRadius: "6px",
                }}
                title="Delete all filtered annotations"
              >
                <Delete />
              </IconButton>
            )}
            header={
              <h2>
                Delete Annotation
                {filteredAnnotations && filteredAnnotations?.length > 1
                  ? "s"
                  : ""}
              </h2>
            }
            content={
              <FlexColContainer
                css={({ spacing }) => css`
                  gap: ${spacing(2)};
                `}
              >
                <FlexRowContainer style={{ justifyContent: "flex-start" }}>
                  <InfoIcon
                    sx={{
                      height: "16px",
                      width: "16px",
                    }}
                  />
                  <p
                    css={({ customTypography }) => css`
                      ${customTypography.paragraph._3}
                    `}
                  >
                    Use the search and filter options to refine the annotations
                    deleted by this action
                  </p>
                </FlexRowContainer>
                {`Are you sure you want to delete all ${filteredAnnotations?.length} displayed annotations?
                This action cannot be undone.`}
              </FlexColContainer>
            }
            footer={
              <PrimaryButton
                onClick={() => {
                  if (filteredAnnotations?.length) {
                    deleteAnnotations.mutate({
                      annotationIds: filteredAnnotations?.map((a) => a.id) as [
                        number,
                        ...number[],
                      ],
                    });
                  }
                }}
              >
                Delete
              </PrimaryButton>
            }
          />
        </FlexRowContainer>
      </FilterHeader>
      <FlexColContainer>
        <div
          css={css`
            padding: 24px 0px 12px 12px;
            position: relative;
            height: 100%;
            width: 100%;
            overflow-y: auto;
          `}
        >
          {isFetching ? (
            <LoadingSpinner />
          ) : (
            <>
              {filteredAnnotations?.length ? (
                Object.entries(groupedAnnotations).map(([date, group]) => (
                  <FlexColContainer
                    key={date}
                    style={{ gap: theme.spacing(2), paddingTop: "14px" }}
                  >
                    <FlexRowContainer>
                      <div
                        style={{
                          ...theme.customTypography.overline._1,
                          width: "max-content",
                        }}
                      >
                        {date.toLocaleUpperCase()}
                      </div>
                      <SeparatorLineHorizontal />
                    </FlexRowContainer>
                    {group.map((annotation) => {
                      let publicationOrFRJ;
                      const publication = publications?.find(
                        (pub) => pub.id === annotation.publicationId,
                      );
                      if (publication) {
                        publicationOrFRJ = publication;
                      } else {
                        if (annotation.articleSlug) {
                          publicationOrFRJ = frjPublication;
                        }
                      }
                      return (
                        <FlexRowContainer
                          css={({ breakpoints }) => css`
                            padding: 0 18px;
                            @media (max-width: ${breakpoints.values.sm}px) {
                              padding: 0;
                              display: flex;
                              flex-direction: column;
                              align-items: flex-start;
                              padding-right: 8px;
                            }
                          `}
                          key={annotation.id}
                        >
                          <div
                            css={css`
                              width: 45px;
                            `}
                          >
                            {annotation.lastModified.toLocaleTimeString(
                              "en-GB",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                          </div>
                          <AnnotationView
                            annotation={annotation}
                            publication={publicationOrFRJ}
                            tags={tags}
                          />
                        </FlexRowContainer>
                      );
                    })}
                  </FlexColContainer>
                ))
              ) : (
                <FlexColContainer>
                  <img
                    src={AnnotationIcon}
                    alt="No annotations"
                    style={{
                      width: "100px",
                      height: "100px",
                      margin: "0 auto",
                      display: "block",
                      opacity: 0.5,
                    }}
                  />
                  <p
                    style={{
                      ...theme.customTypography.jumbo._5,
                      color: theme.colours.accent.mid.dark20,
                      textAlign: "center",
                    }}
                  >
                    {annotations?.length ? (
                      <>
                        No annotations found{" "}
                        {queryValue && (
                          <>
                            for{" "}
                            <span
                              style={{
                                ...theme.customTypography.jumbo._4,
                                fontWeight: 800,
                                color: theme.colours.accent.dark._40,
                              }}
                            >
                              {queryValue}
                            </span>{" "}
                          </>
                        )}
                        in{" "}
                        {selectedPublications.length
                          ? "selected publications"
                          : "all publications"}
                        .
                      </>
                    ) : (
                      "You haven't added any highlights or notes"
                    )}
                  </p>
                </FlexColContainer>
              )}
            </>
          )}
        </div>
      </FlexColContainer>
    </>
  );
};
